import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Logo, useTagline } from "./Logo";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Wallet,
  ClipboardList,
  BookOpen,
  MessageSquare,
  Trophy,
  Sparkles,
  Bell,
  CreditCard,
  CalendarCheck,
  Palette,
  ShieldCheck,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const NAVS = {
  admin: [
    { to: "/dashboard/admin", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/admin/users", icon: Users, label: "Users" },
    { to: "/dashboard/admin/batches", icon: GraduationCap, label: "Batches" },
    { to: "/dashboard/admin/fees", icon: Wallet, label: "Fees" },
    { to: "/dashboard/admin/notifications", icon: Bell, label: "Notify" },
    { to: "/dashboard/admin/subscriptions", icon: CreditCard, label: "Subscriptions" },
    { to: "/dashboard/admin/settings", icon: Palette, label: "Branding" },
    { to: "/dashboard/admin/audit", icon: ShieldCheck, label: "Audit Log" },
    { to: "/dashboard/admin/chat", icon: MessageSquare, label: "Chat" },
    { to: "/dashboard/admin/subscribe", icon: CreditCard, label: "Plans" },
  ],
  teacher: [
    { to: "/dashboard/teacher", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/teacher/attendance", icon: CalendarCheck, label: "Attendance" },
    { to: "/dashboard/teacher/homework", icon: BookOpen, label: "Homework" },
    { to: "/dashboard/teacher/tests", icon: ClipboardList, label: "Tests" },
    { to: "/dashboard/teacher/chat", icon: MessageSquare, label: "Chat" },
  ],
  student: [
    { to: "/dashboard/student", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/student/tests", icon: ClipboardList, label: "Tests" },
    { to: "/dashboard/student/homework", icon: BookOpen, label: "Homework" },
    { to: "/dashboard/student/leaderboard", icon: Trophy, label: "Leaderboard" },
    { to: "/dashboard/student/insights", icon: Sparkles, label: "AI Insights" },
    { to: "/dashboard/student/chat", icon: MessageSquare, label: "Chat" },
    { to: "/dashboard/student/subscribe", icon: CreditCard, label: "Plans" },
  ],
  parent: [
    { to: "/dashboard/parent", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/parent/insights", icon: Sparkles, label: "AI Insights" },
    { to: "/dashboard/parent/chat", icon: MessageSquare, label: "Chat" },
    { to: "/dashboard/parent/subscribe", icon: CreditCard, label: "Plans" },
  ],
};

const ROLE_LABEL = {
  admin: "Administrator",
  teacher: "Teacher",
  student: "Student",
  parent: "Parent",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tagline = useTagline();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;
  const items = NAVS[user.role] || [];

  const NavItem = ({ to, icon: Icon, label, end }) => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setMobileOpen(false)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive
            ? "bg-blue-600 text-white shadow-sm"
            : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2.2} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-5 py-5 border-b border-slate-200">
          <Logo />
          <p className="mt-2 text-xs text-slate-500 italic">"{tagline}"</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavItem key={it.to} {...it} />
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
            <span className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
              {ROLE_LABEL[user.role]}
            </span>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              const wasAdmin = user.role === "admin";
              await logout();
              navigate(wasAdmin ? "/admin-login" : "/login");
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 ym-glass">
        <div className="flex items-center justify-between px-4 h-14">
          <Logo size="sm" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 rounded-lg text-slate-600 hover:bg-blue-50"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="border-t border-slate-200 bg-white px-3 py-3 space-y-1">
            {items.map((it) => (
              <NavItem key={it.to} {...it} />
            ))}
            <button
              data-testid="logout-button-mobile"
              onClick={async () => {
                const wasAdmin = user.role === "admin";
                await logout();
                navigate(wasAdmin ? "/admin-login" : "/login");
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-red-50 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
      </div>

      {/* Main */}
      <main className="flex-1 lg:pl-0 pt-14 lg:pt-0">
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
